import { ReactNode } from "react";
import { Box, IconButton, Stack, Typography } from "@mui/material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import LanguageSwitcher from "../language";
import BrandLogo from "../BrandLogo";
import Layout from "../../Layout";
import { useCustomColors } from "../../hooks/useCustomColors";
import { CARD_PADDING, PAGE_GUTTER } from "../../utils/spacing";
import { useNavigate } from "react-router-dom";

export interface AuthCardProps {
  title: string;
  subtitle: string;
  children: ReactNode;
  footer?: ReactNode;
  onClose?: () => void;
  embedded?: boolean;
}

const AuthCard = ({
  title,
  subtitle,
  children,
  footer,
  onClose,
  embedded = false,
}: AuthCardProps) => {
  const colors = useCustomColors();
  const navigate = useNavigate();
  const card = (
    <Box sx={{ width: "100%", maxWidth: "470px", mx: "auto", display: "flex", flexDirection: "column", borderRadius: "24px", p: CARD_PADDING, border: `1px solid ${colors.border.accent}`, boxShadow: `${colors.card.shadow}, ${colors.glow.cardInner}`, background: `radial-gradient(circle at 12% 0%, ${colors.tint.primaryStrong} 0%, transparent 58%), radial-gradient(circle at 92% 8%, ${colors.tint.primary} 0%, transparent 52%), ${colors.card.gradient}` }}>
      <Stack direction="row" alignItems="center" sx={{ gap: "12px", mb: "20px" }}>
        <BrandLogo height={34} onClick={() => navigate("/")} />
        {onClose && (
          <Stack direction="row" alignItems="center" sx={{ ml: "auto", gap: "4px" }}>
            <Box sx={{ "& .MuiButton-root": { mt: 0 } }}><LanguageSwitcher /></Box>
            <IconButton aria-label="close" onClick={onClose} sx={{ width: 44, height: 44, color: colors.text.secondary }}><CloseRoundedIcon /></IconButton>
          </Stack>
        )}
      </Stack>
      <Typography sx={{ fontFamily: "Montserrat, sans-serif", fontSize: { xs: "30px", md: "36px" }, fontWeight: 800, fontStyle: "italic", lineHeight: 1.15, color: colors.accent.gold }}>{title}</Typography>
      <Typography sx={{ mt: "8px", fontSize: "13.5px", color: colors.text.secondary }}>{subtitle}</Typography>
      <Box sx={{ mt: "22px" }}>{children}</Box>
      {footer && <Box sx={{ mt: "22px", p: "18px", flexShrink: 0, borderRadius: "14px", textAlign: "center", backgroundColor: colors.background.panelSecondary, border: `1px solid ${colors.border.accent}` }}>{footer}</Box>}
    </Box>
  );

  return embedded ? card : <Layout><Box sx={{ minHeight: "100vh", background: colors.background.gradient, display: "flex", alignItems: "center", justifyContent: "center", p: PAGE_GUTTER }}>{card}</Box></Layout>;
};

export default AuthCard;
